import React, { FC } from 'react';
import './MandateForm.css';

import {
	InputField,
	SelectField,
	TextAreaField,
	FaCalendarAlt,
	FaFileInvoice,
	FaSearchDollar,
	FaUser,
	RequiredNote,
	FaBuilding,
	DomainFormProps,
	FaBullseye,
	SelectOptionType,
	FaMoneyBillAlt,
} from '@/Shared';
import { MandateFormViewModel } from '../../Logic/ViewModels';
import { MultiValue, SingleValue } from 'react-select';


/**
 * =====================================================
 * Component: MandateForm
 * =====================================================
 *
 *
 * Domain-specific form UI for Mandate.
 * Mounted inside GenericDomainForm.
 *
 * Responsibilities:
 * - Render mandate fields
 * - Use data from formVM (options, values)
 *
 * No permissions, no loading, no orchestration logic here.
 *
 * Author: Samano CASTRE
 * Date: February 15, 2026
 */
const MandateForm: FC<DomainFormProps> = ({
	domainFormHook,
	formRef,
	formikProps
}) => {

	const entityFormVM: MandateFormViewModel = domainFormHook.entityFormVM;
	const { setFieldValue, values } = formikProps;

	const updateFields = (computedValues: Record<string, any>) => {
		Object.entries(computedValues).forEach(
			([key, value]) => setFieldValue(key, value, true)
		);
	}


	/**
	 * =====================================================
	 * Main Form Rendering
	 * =====================================================
	 *
	 * Fieldsets organize the form into logical sections for clarity.
	 */
	return (
		<div className="fieldset-wrapper">

			<fieldset>
				<legend>Détails du Mandat</legend>
				<SelectField
					label='Opération'
					required
					icon={<FaFileInvoice />}
					name='operationType'
					placeholder="Choisir une opération"
					options={entityFormVM.operationOptions}
					scrollParent={formRef.current}
					onChange={
						(selectedOptions) => {
							const value = (selectedOptions as SingleValue<SelectOptionType>)?.value as string;
							entityFormVM.handleOperationTypeChange(value);
							updateFields({
								operationType: value,
								mandateType: "",
								propertyId: "",
								needId: ""
							});
						}
					}
				/>
				<SelectField
					label="Type Mandat"
					name="mandateType"
					icon={<FaBullseye />}
					options={entityFormVM.mandateTypeOptions}
					scrollParent={formRef.current}
					placeholder="Choisir un type de mandat"
					onChange={
						(selectedOptions) => {
							const value = (selectedOptions as SingleValue<SelectOptionType>)?.value as string;
							entityFormVM.handleMandateTypeChange(value);
							updateFields({
								mandateType: value,
								propertyId: "",
								needId: ""
							});
						}
					}
					required
				/>

				{
					entityFormVM.hasValidPropertyOptions() && <SelectField
						label='Bien (Cas de biens)'
						icon={<FaBuilding />}
						name='propertyId'
						placeholder="Choisir un bien"
						options={entityFormVM.propertyValidOptions}
						scrollParent={formRef.current}
						required
						onChange={
							(selectedOptions) => {
								updateFields(entityFormVM.handleComputeAgencyComTtc({
									propertyId: (selectedOptions as SingleValue<SelectOptionType>)?.value,
									feeRate: values.feeRate,
									applicableVat: values.applicableVat
								}));
							}
						}
					/>
				}
				{entityFormVM.hasValidNeedOptions() &&
					<SelectField
						label='Besoin (Cas de Recherche de biens)'
						icon={<FaBuilding />}
						name='needId'
						options={entityFormVM.needValidOptions}
						placeholder="Choisir un besoin"
						scrollParent={formRef.current}
						required
						/*
						onChange={
							(selectedOptions) => {
								const value = (selectedOptions as SingleValue<SelectOptionType>)?.value;
								updateFields(entityFormVM.handleComputeAgencyComTtc({
									needId : value,
									properties: values.properties,
									feeRate: values.feeRate,
									applicableVat: values.applicableVat
								}));
							}
						}*/

					/>
				}

				<InputField
					required
					label='Date début'
					icon={<FaCalendarAlt />}
					name='startAt'
					type='date'
					autoComplete='date' />

				<InputField
					required
					label='Date de fin'
					icon={<FaCalendarAlt />}
					name='endAt'
					type='date'
					autoComplete='date' />

				<RequiredNote />
			</fieldset>
			
			<fieldset>
				<legend>Eléments Financiers</legend>
				<InputField
					required
					label='Taux Honoraires'
					icon={<FaSearchDollar />}
					name='feeRate'
					type='number'
					autoComplete='on'
					step={0.5}
					onChange={(e) =>
						updateFields(
							entityFormVM.handleComputeAgencyComTtc({
								propertyId: values.propertyId,
								feeRate: Number(e.target.value),
								applicableVat: values.applicableVat
							})
						)
					}
				/>

				<InputField
					required
					label='TVA Applicable'
					icon={<FaSearchDollar />}
					name='applicableVat'
					type='number'
					autoComplete='on'
					step={0.5}
					onChange={(e) =>
						updateFields(entityFormVM.handleComputeAgencyComTtc({
							propertyId: values.propertyId,
							feeRate: values.feeRate,
							applicableVat: Number(e.target.value)
						}))
					}
				/>
				<InputField
					label='Montant Total (Prix + TVA)'
					icon={<FaMoneyBillAlt />}
					name='totalPriceTtc'
					placeholder='Montant'
					type='text'
					autoComplete='on'
					readOnly
				/>
				<InputField
					label='Com. Agence (Honor. * Prix + TVA)'
					icon={<FaMoneyBillAlt />}
					name='agencyComTtc'
					placeholder='Commission agence'
					type='text'
					autoComplete='on'
					readOnly
				/>

			</fieldset>
			<fieldset>
				<legend>Parties Prenantes</legend>
				<SelectField
					label='Mandant(s)'
					icon={<FaUser />}
					name='contacts'
					required
					placeholder="Choisir un propriétaire"
					options={entityFormVM.ownerOptions}
					scrollParent={formRef.current}
					isMulti
				/>

				<SelectField
					label='Référent'
					icon={<FaUser />}
					name='agentId'
					placeholder="Choisir un référent"
					options={entityFormVM.userOptions}
					required
					scrollParent={formRef.current}
				/>
				<InputField
					label='Apporteur'
					placeholder="Saisir le nom de l'apporteur..."
					icon={<FaUser />}
					name='referrer'
					type='text'
					autoComplete='on' />

				<TextAreaField
					name="notes"
					label='Notes'
					placeholder="Saisir quelque chose..."
				/>
				<RequiredNote />
			</fieldset>
		</div>

	);
}

export default MandateForm;
